import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import AppShell from "./appShell";
import { ShellNavProvider } from "./shellNav";
import DashboardFilters from "../../reusableComponents/dashboardFilters/dashboardFilters";
import { ContextPopover } from "../../reusableComponents/contextSwitcher/contextSwitcher";
import DashboardChooser from "./dashboardChooser";
// LEGACY — delete this import and the isLegacy branches to remove it.
import LegacySelectionView from "./legacy/legacySelectionView";
import LegacyFilters from "./legacy/legacyFilters";
import { legacyChromeFor } from "./legacy/legacyScreens";
import {
  contextIdOf,
  type RecentContext,
} from "../../reusableComponents/contextSwitcher/contextSwitcherModel";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import useNavConfig from "../../../hooks/useNavConfig";
import { tokens } from "../../../theme";
import { toShellUser } from "./shellChrome";
import {
  Program,
  programForUserType,
  userTypeNeedsSalesRep,
  userTypeNeedsSchoolDealer,
} from "../../../models/program";
import { RoleIndex } from "../../../models/role";

import { logoutUser } from "../../../store/thunks/authenticationThunk";
import { logout } from "../../../store/features/authentication/authenticationSlice";
import { systemManagementReset } from "../../../store/features/systemManagement/systemManagementSlice";
import {
  portalManagementReset,
  portalManagementSearchReset,
  recordRecentContext,
  setContextCommitted,
  setFilteredUserTypes,
  setSelectedClient,
  setSelectedProgram,
  setSelectedUserType,
} from "../../../store/features/portalManagement/portalManagementSlice";
import { persistor } from "../../../store";

// Rail collapse is UI state, not app state, so it stays out of Redux (and out of
// the persisted slices). localStorage only so it survives navigating between
// /dashboard and /systemManagement, which remounts this component.
const COLLAPSE_KEY = "zintoro.rail.collapsed";

const readCollapsed = () => {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
};

interface PortalShellProps {
  /** System Management renders the dark rail and the system nav group. */
  system?: boolean;
  /**
   * Context chooser: no client is selected yet, so the rail carries no program
   * nav — only System Management and the account row.
   */
  chooser?: boolean;
  children: React.ReactNode;
}

/**
 * Mounts the shell for the authenticated area. Owns the rail selection and
 * publishes it through ShellNavContext, so the program views render content only
 * and never remount when the selection changes.
 *
 * Replaces the chrome previously spread across header.tsx (logged-in branch),
 * dashboardHeader.tsx and the four per-view tab bars.
 */
const PortalShell = ({ system = false, chooser = false, children }: PortalShellProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // The legacy theme reaches context selection through a full-page form, the way
  // the app did before the refactor — not the modal the new shells use.
  const isLegacy = tokens.shell === "legacy";
  const [switcherAnchor, setSwitcherAnchor] = useState<HTMLElement | null>(null);
  const [chooserOpen, setChooserOpen] = useState(false);

  const baseNav = useNavConfig({ system });

  // While selecting, selectedProgram may still hold the previous context, which
  // would draw nav for a dashboard the user is in the middle of leaving. Legacy
  // additionally replaces the content with its form, and the original showed no
  // tabs on that screen.
  const selecting = chooser || (isLegacy && chooserOpen);

  const nav = useMemo(
    () =>
      selecting
        ? { ...baseNav, groups: [], otherPrograms: [], defaultItemId: "" }
        : baseNav,
    [selecting, baseNav]
  );
  const { user } = useAppSelector((s: any) => s.authentication);
  const {
    selectedClient,
    selectedProgram,
    selectedUserType,
    selectedLastUpdated,
    clients,
    userTypes,
    recentContexts,
    defaultContext,
  } = useAppSelector((s: any) => s.portalManagement);

  // Older persisted states predate recentContexts; treat a missing list as empty
  // rather than letting the popover read .length off undefined. The active mark
  // is derived here, not stored: a recent is "active" only relative to what is
  // committed right now.
  const currentContextId = contextIdOf(selectedClient, selectedProgram, selectedUserType);
  const recents: RecentContext[] = useMemo(() => {
    const list: RecentContext[] = Array.isArray(recentContexts) ? recentContexts : [];
    return list.map((r) => ({ ...r, active: r.id === currentContextId }));
  }, [recentContexts, currentContextId]);

  const [selectedItemId, setSelectedItemId] = useState(nav.defaultItemId);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggleCollapsed = () =>
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* private mode / quota — the toggle still works for this session */
      }
      return next;
    });

  const validIds = useMemo(
    () => nav.groups.flatMap((g) => g.items.map((i) => i.id)),
    [nav.groups]
  );

  // Role gating and program changes rewrite the rail, which can strand the
  // selection on an item that no longer exists. Fall back to the new default.
  useEffect(() => {
    if (validIds.length && !validIds.includes(selectedItemId)) {
      setSelectedItemId(nav.defaultItemId);
    }
  }, [validIds, selectedItemId, nav.defaultItemId]);

  const shellNav = useMemo(
    () => ({ selectedItemId, selectItem: setSelectedItemId }),
    [selectedItemId]
  );

  const railUser = useMemo(() => toShellUser(user), [user]);

  // Most client users never pass through the chooser, so selectedProgram is
  // null for them — deliberately, because setSelectedProgram clears the sales
  // rep. Their program comes from their client type instead, the same
  // derivation dashboardView uses; reading only the store here left them with
  // no Branch or Department filters at all. A Manager does pass through the
  // chooser (clientUserPicksContext), and the store value they committed wins.
  const programIndex =
    selectedProgram?.program_index ?? programForUserType(user?.client_type_name);
  // The worklist (sales-rep view) has its own Branch/Sales-rep selects; the
  // shell's Branch/Department filters do nothing there. Legacy encodes this in
  // legacyScreens (filters: false) — the archetype is the refreshed gate.
  const isAnalytics =
    programIndex === Program.Index.Analytics && nav.archetype !== "worklist";

  // LEGACY — each original screen carried its own chrome; this replays that.
  const legacyChrome = legacyChromeFor(
    selectedProgram?.program_index,
    selectedItemId,
    nav.archetype,
    system
  );

  const title = system
    ? "System Management"
    : chooser
    ? "Choose a dashboard"
    : selectedClient?.customer_name ?? "Dashboard";

  // Everything dashboardHeader.tsx stacked as three lines, on one mono row.
  const meta = useMemo(() => {
    if (system || chooser) return undefined;
    return [
      selectedClient?.currency ? `Currency ${selectedClient.currency}` : null,
      selectedClient?.create_date
        ? `Since ${dayjs(selectedClient.create_date).format("YYYY-MM-DD")}`
        : null,
      selectedLastUpdated ? `Updated ${selectedLastUpdated}` : null,
    ]
      .filter(Boolean)
      .join("  ·  ");
  }, [system, chooser, selectedClient, selectedLastUpdated]);

  const openChooser = () => {
    setSwitcherAnchor(null);
    setChooserOpen(true);
  };

  // A recent carries the ids, so re-opening one applies directly — client,
  // program AND the adopted user type, or the card would open a different
  // dashboard than it describes (a "Sales rep · X" recent used to open plain
  // AnalyticsView). Returns false when the context cannot be rebuilt: the
  // lists are not loaded, the type no longer exists, or the type needs a
  // rep/dealer scope — the scope object lives only in the chooser's fetches,
  // so restoring it here would commit a context with the scope silently null.
  const applyRecentContext = useCallback(
    (context: RecentContext): boolean => {
      const client = (clients ?? []).find((c: any) => c.client_index === context.clientIndex);
      const userType =
        context.userTypeIndex == null
          ? undefined
          : (userTypes ?? []).find(
              (t: any) => t.client_user_type_index === context.userTypeIndex
            );
      const needsScope =
        userTypeNeedsSalesRep(context.userTypeIndex) ||
        userTypeNeedsSchoolDealer(context.userTypeIndex);
      if (!client || (context.userTypeIndex != null && !userType) || needsScope) {
        return false;
      }
      dispatch(setSelectedClient(client));
      // The program is synthesized from the recent, not looked up:
      // setSelectedClient clears state.programs (they are per-client), so the
      // list is empty after every commit and a lookup could never succeed.
      // Nothing downstream reads more than program_index (display falls back
      // through Program.DisplayName), so these two fields are the contract.
      // setSelectedProgram clears the user type and scopes, so it goes before
      // them.
      dispatch(
        setSelectedProgram({
          program_index: context.programIndex,
          program_name: context.program,
        })
      );
      if (userType) {
        dispatch(setSelectedUserType(userType));
        dispatch(
          setFilteredUserTypes(
            (userTypes ?? []).filter((t: any) => t.program_index === context.programIndex)
          )
        );
      }
      dispatch(portalManagementSearchReset());
      // Re-record so the list reorders to most-recent-first.
      dispatch(recordRecentContext({ ...context, active: undefined }));
      dispatch(setContextCommitted(true));
      return true;
    },
    [clients, userTypes, dispatch]
  );

  // Portal management keeps this shell mounted, so the last rail tab would
  // otherwise stick. Re-entering a dashboard always starts on the default
  // item (Analytics: Dashboard). Use baseNav — while selecting, nav.default
  // is cleared so the rail stays empty.
  const enterDashboard = () => setSelectedItemId(baseNav.defaultItemId);

  const handlePickRecent = (context: RecentContext) => {
    setSwitcherAnchor(null);
    enterDashboard();
    // Already-active recent: do not re-apply. searchReset would wipe tiles,
    // then the same customer_id would look "already fetched."
    if (context.active) return;
    if (!applyRecentContext(context)) setChooserOpen(true);
  };

  // "Make this my default": open the saved context instead of the chooser
  // whenever the shell is in chooser mode with a default on record — a session
  // that expired and was re-established, a snapshot predating contextCommitted,
  // or a return from System Management (whose bootstrap resets the portal
  // slice; the reset deliberately preserves recents and default). NOT an
  // explicit logout: handleLogout purges, and the PURGE handler returns
  // initialState — recents and default name other clients and should not
  // survive a sign-out on a shared machine.
  //
  // Client users only, matching where the checkbox is offered. Their chooser
  // self-seeds everything the resolution needs (their one client, then its
  // programs), so this settles moments after landing. An admin's lists are
  // only fetched as they step through the chooser, so an admin default could
  // resolve mid-pick and hijack the dialog — admins keep recents instead. If
  // the default cannot be rebuilt (see applyRecentContext), the chooser simply
  // stays. Legacy has no default checkbox.
  const isClientUser = user?.role_index === RoleIndex.ClientUser;
  useEffect(() => {
    if (!chooser || isLegacy || !defaultContext || !isClientUser) return;
    applyRecentContext(defaultContext);
  }, [chooser, isLegacy, defaultContext, isClientUser, applyRecentContext]);

  // Jumping to another program opens the chooser rather than re-implementing
  // its load sequence here — picking a program has to refilter user types and
  // may need a sales rep, and DashboardChooser already owns all of that.
  const handleSelectProgram = () => openChooser();

  const handleLogout = () => {
    dispatch(logoutUser({ user_index: user?.client_user_index }));
    dispatch(portalManagementReset());
    dispatch(systemManagementReset());
    dispatch(logout());
    persistor.purge();
    navigate("/login");
  };

  return (
    <AppShell
      nav={nav}
      selectedItemId={selectedItemId}
      onSelectItem={setSelectedItemId}
      variant={system ? "system" : "client"}
      contextLabel={selectedClient?.customer_name}
      onExitSystem={() => navigate("/dashboard")}
      user={railUser}
      onSelectProgram={handleSelectProgram}
      onOpenSystemManagement={() => navigate("/systemManagement")}
      onLogout={handleLogout}
      collapsed={collapsed}
      onToggleCollapsed={toggleCollapsed}
      title={title}
      meta={meta}
      filters={
        isAnalytics && !system && !selecting
          ? isLegacy
            ? legacyChrome.filters
              ? <LegacyFilters />
              : undefined
            : <DashboardFilters />
          : undefined
      }
      hideBar={isLegacy ? selecting || !legacyChrome.title : false}
      plainMain={selecting || (isLegacy && !legacyChrome.tint)}
      legacyChrome={legacyChrome}
      onOpenSwitcher={
        !nav.canSwitchContext || system || selecting
          ? undefined
          : isLegacy
          ? () => setChooserOpen(true)
          : setSwitcherAnchor
      }
    >
      <ShellNavProvider value={shellNav}>
        {isLegacy && selecting ? (
          <LegacySelectionView
            onDone={() => {
              dispatch(setContextCommitted(true));
              enterDashboard();
              setChooserOpen(false);
            }}
          />
        ) : (
          children
        )}
      </ShellNavProvider>
      {!isLegacy && (
      <ContextPopover
        anchorEl={switcherAnchor}
        onClose={() => setSwitcherAnchor(null)}
        recents={recents}
        onPickRecent={handlePickRecent}
        onOpenChooser={openChooser}
        onOpenSystemManagement={
          nav.showSystemManagement
            ? () => {
                setSwitcherAnchor(null);
                navigate("/systemManagement");
              }
            : undefined
        }
      />
      )}
      {!isLegacy && (
        <DashboardChooser
          open={chooser || chooserOpen}
          onClose={chooser ? undefined : () => setChooserOpen(false)}
          onCommitted={enterDashboard}
        />
      )}
    </AppShell>
  );
};

export default PortalShell;
